import { Reference, StoreObject } from '@apollo/client';
import * as Sentry from '@sentry/react';

// GQL
import { client } from '../gql/client';
import { DELETE_ACTION_BY_ID } from '../gql/mutations/actions';
import { GET_ORDER_BY_ID } from '../gql/queries/orders/orders';
import { esErrorTransitorioDeBackend } from '../gql/retryableBackendCodes';
import { showPreviousDraftDiscardedToast } from './toasts';

// Constants
import { formActionNaming } from '../components/forms/ActionForms/constants';

// Types
import { OrderState } from '../types/orders';

// Utils
import { decompressSessionStorage } from './sessionStorage';

/** Lo que hace falta para descartar un borrador: sus dos ids y su padre. */
export interface ActionDraftPointer {
  actionId: string;
  orderId: string;
  orderParentId?: string;
}

/**
 * Lee el puntero al borrador del asistente ANTERIOR de esta pestaña
 * (`new-action-data`). Síncrono: quien llama lo captura ANTES de vaciar el
 * almacenamiento con `removeNewActionStorage()`.
 */
export const readPreviousActionDraft = (): ActionDraftPointer | null => {
  const raw = sessionStorage.getItem(formActionNaming.NEW_ACTION_DATA);
  if (!raw) return null;
  try {
    const data = JSON.parse(decompressSessionStorage(raw));
    return data?.actionId && data?.orderId
      ? {
          actionId: data.actionId,
          orderId: data.orderId,
          orderParentId: data.orderParentId,
        }
      : null;
  } catch {
    return null;
  }
};

/** Reintentos ante un aborto transitorio del backend (P2034, timeouts). */
const RETRY_DELAYS_MS = [400, 1200];

const wait = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

/**
 * El descarte que siga en vuelo (remate del 15-oct, QA T3.4+a / T3.5+a).
 *
 * `createBasicAction` lanza el descarte SIN esperarlo (entrar al asistente no
 * espera a la red), y una cadena NUEVA lanzada enseguida creaba su básica
 * mientras ese borrado seguía escribiendo la misma Order del recorrido: P2034
 * y ni básica ni aviso. Se guarda aquí, encadenado (un descarte espera al
 * anterior), y la cadena nueva lo espera con `waitForPendingDraftDiscard`.
 */
let pendingDiscard: Promise<void> = Promise.resolve();

/**
 * Tope de esa espera: la cadena también la usa el cronómetro, en carretera, y
 * un descarte colgado por mala cobertura no puede dejar al operario sin
 * asistente. El descarte cabe de sobra (consulta + borrado + 1,6 s de
 * reintentos); pasado el tope se crea igual y, si chocara, el alta reintenta.
 */
const PENDING_DISCARD_MAX_WAIT_MS = 5_000;

/** Espera al descarte que siga en vuelo, como mucho `maxMs`. Nunca falla. */
export const waitForPendingDraftDiscard = (
  maxMs: number = PENDING_DISCARD_MAX_WAIT_MS
): Promise<void> => {
  let timer: ReturnType<typeof setTimeout> | undefined;
  const cap = new Promise<void>((resolve) => {
    timer = setTimeout(resolve, maxMs);
  });
  return Promise.race([pendingDiscard, cap]).finally(() => clearTimeout(timer));
};

/**
 * Descarta un borrador: si la actuación sigue `partial`, la borra.
 *
 * 🔴 Si choca con otra escritura de la MISMA Order del recorrido, el backend
 * aborta con «Conflicto con otra operación simultánea» (P2034,
 * `SAVE_TX_CONFLICT`). Pasaba cuando el borrado iba EN PARALELO con el
 * `SAVE_ACTION` de la básica nueva (medido en beta el 2026-10-09, Su.0000620 y
 * Su.0000615): el borrado se perdía y el borrador quedaba huérfano. Por eso
 * `createBasicAction` borra DESPUÉS de crear, y aquí se reintenta lo
 * transitorio. Cualquier otro fallo (o sin red) se registra y no se borra.
 */
export const discardPartialActionDraft = (
  draft: ActionDraftPointer | null
): Promise<void> => {
  if (!draft) return Promise.resolve();
  // Queda guardado para que la siguiente cadena lo espere. Nunca rechaza: los
  // fallos se registran dentro.
  const run = pendingDiscard.then(() => discardWithRetries(draft));
  pendingDiscard = run;
  return run;
};

const discardWithRetries = async (draft: ActionDraftPointer): Promise<void> => {
  for (let attempt = 0; ; attempt++) {
    try {
      await discardOnce(draft);
      return;
    } catch (e) {
      if (attempt < RETRY_DELAYS_MS.length && esErrorTransitorioDeBackend(e)) {
        await wait(RETRY_DELAYS_MS[attempt]);
        continue;
      }
      Sentry.captureException(e);
      console.log(e);
      return;
    }
  }
};

/**
 * Descarta el borrador que dejó a medias el asistente ANTERIOR de esta
 * pestaña. Lo usa «Crear Actuación» de `ShowOT`, que lo espera ANTES de crear
 * (en serie, sin choque). No toca el almacenamiento: quien llama limpia
 * después con `removeNewActionStorage()`.
 *
 * Vivía dentro del «Crear Actuación» de `ShowOT`. Las otras dos puertas que
 * crean una básica del recorrido (la causa «Actuación» al parar el cronómetro
 * y la pill «Vincular a actuación» del tramo) llaman a `createBasicAction`,
 * que vaciaba el puntero SIN borrar la parcial: esa quedaba huérfana para
 * siempre, sin capítulo, y bloqueaba validar el recorrido (diagnóstico
 * docs/superpowers/specs/2026-10-08-rv-parciales-diagnostico.md § 7).
 *
 * Sin red no borra nada (la consulta del estado falla y se registra).
 */
export const discardPreviousPartialAction = async (): Promise<void> =>
  discardPartialActionDraft(readPreviousActionDraft());

const discardOnce = async (previousData: ActionDraftPointer): Promise<void> => {
  const {
    data: {
      order: { state, roid },
    },
  } = await client.query({
    query: GET_ORDER_BY_ID,
    variables: {
      id: previousData.orderId,
    },
    fetchPolicy: 'network-only',
  });
  if (state !== OrderState.Partial) return;
  await client.mutate({
    mutation: DELETE_ACTION_BY_ID,
    variables: {
      id: previousData.actionId,
    },
    update(cache) {
      cache.modify({
        fields: {
          actions(existingActions = {}, { readField }) {
            const filterActions = existingActions.actions.filter(
              (action: StoreObject | Reference | null) => {
                if (action === null) return false;
                const ref = readField('id', action);
                return ref !== previousData.actionId;
              }
            );

            return {
              ...existingActions,
              actions: filterActions,
              totalCount: existingActions.totalCount - 1,
              pageInfo: {},
            };
          },
        },
      });

      cache.modify({
        id: `Order:${previousData.orderParentId}`,
        fields: {
          childExpeditionOrderIDs(existingOrders = []) {
            // RV (2026-07-21): quitar EXACTAMENTE la Order borrada
            // (previousData.orderId). La heurística anterior
            // ("el primer hijo action+partial de la caché") era
            // correcta en work-orders (máx. 1 parcial por padre)
            // pero en el RV conviven varias básicas parciales
            // legítimas y podía esconder la card equivocada hasta
            // el siguiente refetch de red.
            return existingOrders.filter(
              (order: string) => order !== previousData.orderId
            );
          },
        },
      });
    },
  });
  // Que el operario sepa qué se ha borrado (decisión del usuario, 2026-10-09).
  if (roid) showPreviousDraftDiscardedToast(String(roid));
};
